import { Link } from 'react-router-dom';

export default function TechnicalSpecs() {
  const metrics = [
    { icon: 'ri-flashlight-line', title: 'Energy-Conscious Systems', desc: 'Designed for efficient, low-impact operation across diverse climates and use patterns.' },
    { icon: 'ri-temp-hot-line', title: 'Insulated Construction', desc: 'Multi-layer thermal protection built into the structural envelope for year-round comfort.' },
    { icon: 'ri-volume-up-line', title: 'Acoustic Comfort', desc: 'Construction and glazing selected to support quiet interiors in varied environments.' },
    { icon: 'ri-shield-check-line', title: 'Weather-Conscious Materials', desc: 'Material systems chosen for durability across a range of weather and exposure conditions.' },
  ];

  const materials = [
    { icon: 'ri-shield-line', text: 'Galvanized Steel Frame Structure 5.0' },
    { icon: 'ri-temp-cold-line', text: 'Multi-layer thermal insulation system' },
    { icon: 'ri-window-line', text: 'Double-glazed low-E glass windows' },
    { icon: 'ri-cpu-line', text: 'Integrated smart home automation' }
  ];

  return (
    <section id="specs" className="relative py-32 px-8 bg-gradient-to-b from-[#000000] to-[#0a0a0f]">
      {/* Grid pattern background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(rgba(0,217,255,0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(0,217,255,0.2) 1px, transparent 1px)',
          backgroundSize: '30px 30px'
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-20">
          <h2 className="text-5xl font-thin mb-4">Engineering Excellence</h2>
          <p className="text-gray-400 text-lg">Precision-crafted for performance and longevity</p>
        </div>

        {/* Three Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
          {/* Left - Dimensions */}
          <div className="lg:col-span-4 bg-gradient-to-br from-[#14141e]/60 to-[#0a0a0f]/60 backdrop-blur-xl border border-cyan-500/30 rounded-lg p-8">
            <h3 className="text-2xl font-medium mb-8 text-white">Dimensional Specs</h3>
            
            {/* 3D Wireframe Placeholder */}
            <div className="relative w-full h-64 mb-8 flex items-center justify-center">
              <div className="relative w-48 h-48">
                <img 
                  src="/images/ai-3d-wireframe-technical-blueprint-drawing-35e535.webp" 
                  alt="3D Wireframe"
                  className="w-full h-full object-contain animate-pulse"
                />
              </div>
            </div>

            {/* Measurements */}
            <div className="space-y-4">
              <div className="flex justify-between items-center pb-3 border-b border-gray-700">
                <span className="text-gray-400">Length</span>
                <span className="text-cyan-400 font-medium text-lg">6.0m</span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b border-gray-700">
                <span className="text-gray-400">Width</span>
                <span className="text-cyan-400 font-medium text-lg">3.3m</span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b border-gray-700">
                <span className="text-gray-400">Height</span>
                <span className="text-cyan-400 font-medium text-lg">3.2m</span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b border-gray-700">
                <span className="text-gray-400">Weight</span>
                <span className="text-cyan-400 font-medium text-lg">3,200kg</span>
              </div>
            </div>
          </div>

          {/* Center - Performance Metrics */}
          <div className="lg:col-span-4 bg-gradient-to-br from-[#14141e]/60 to-[#0a0a0f]/60 backdrop-blur-xl border border-cyan-500/30 rounded-lg p-8">
            <h3 className="text-2xl font-medium mb-8 text-white">Performance Metrics</h3>
            
            <div className="grid grid-cols-2 gap-8">
              {metrics.map((metric, index) => (
                <div key={index} className="flex flex-col items-center text-center group">
                  <div className="w-14 h-14 flex items-center justify-center mb-3">
                    <i className={`${metric.icon} text-3xl text-cyan-400`}></i>
                  </div>
                  <h4 className="text-sm font-medium text-white mb-2">{metric.title}</h4>
                  <p className="text-xs text-gray-500 leading-relaxed">{metric.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Materials & Systems */}
          <div className="lg:col-span-4 bg-gradient-to-br from-[#14141e]/60 to-[#0a0a0f]/60 backdrop-blur-xl border border-cyan-500/30 rounded-lg p-8">
            <h3 className="text-2xl font-medium mb-8 text-white">Materials & Systems</h3>
            
            <div className="space-y-6">
              {materials.map((material, index) => (
                <div key={index} className="flex items-start gap-4 group cursor-pointer">
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                    <i className={`${material.icon} text-2xl text-cyan-400 group-hover:scale-110 transition-transform`}></i>
                  </div>
                  <p className="text-gray-300 leading-relaxed group-hover:text-white transition-colors">{material.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Comparison Button */}
        <div className="text-center">
          <Link 
            to="/product-specifications"
            className="px-8 py-4 bg-cyan-500/20 backdrop-blur-md border border-cyan-400/50 rounded-full text-cyan-400 hover:bg-cyan-500/30 transition-all hover:shadow-[0_0_20px_rgba(0,217,255,0.3)] whitespace-nowrap cursor-pointer inline-block"
          >
            View Full Specifications
          </Link>
        </div>
      </div>
    </section>
  );
}